import { ARTIFACTS_PATH, isJobActive, type CandidateBatchResource, type StoreDesignDetail } from '@studio/shared';
import { useEffect, useRef, useState, type ReactNode } from 'react';

type ProgressValue = Pick<StoreDesignDetail, 'icon' | 'screenshots' | 'jobs' | 'progress'>;

function batchWork(value: ProgressValue, batch: CandidateBatchResource, icon: boolean) {
  const jobFor = (jobId: string | null) => value.jobs.find((job) => job.id === jobId);
  const active = batch.slots.some((slot) => slot.state === 'working' && isJobActive(jobFor(slot.jobId)?.state ?? 'queued'));
  const working = batch.slots.some((slot) => slot.state === 'working' && jobFor(slot.jobId)?.state === 'working');
  const ready = batch.slots.every((slot) => slot.state === 'ready');
  const interrupted = batch.slots.some((slot) => slot.state !== 'ready' && (slot.state !== 'working'
    || !isJobActive(jobFor(slot.jobId)?.state ?? 'queued')));
  const progress = batch.slots.flatMap((slot) => value.progress.filter((entry) => entry.slotId === slot.id));
  const previews = icon ? [] : batch.slots.filter((slot) => slot.state !== 'ready').flatMap((slot) =>
    value.progress.find((entry) => entry.slotId === slot.id)?.images.map((image) => ({ ...image, slotId: slot.id,
      position: batch.scope === 'slide' ? batch.scopeItemPosition ?? image.position : image.position })) ?? []);
  const completed = progress.reduce((sum, entry) => sum + entry.completed, 0);
  const total = progress.reduce((sum, entry) => sum + entry.total, 0);
  const label = batch.scope === 'slide' ? `Правка скриншота ${(batch.scopeItemPosition ?? 0) + 1}`
    : batch.kind === 'branch' ? icon ? 'Доработка иконки' : 'Доработка скриншотов' : icon ? 'Иконка' : 'Скриншоты';
  const stage = ready ? 'Готово' : working ? completed === total ? 'Собираем вариант'
    : !icon && batch.slots.length === 1 && total > 1 ? `Создаётся скриншот ${Math.min(completed + 1, total)} из ${total}`
      : icon ? batch.slots.length === 1 ? 'Создаётся иконка' : 'Создаются варианты иконки' : 'Создаются скриншоты'
    : active ? 'В очереди' : 'Требуется внимание';
  return { id: batch.id, label, active, interrupted, stage, completed, total, previews,
    since: Math.min(...progress.map((entry) => Date.parse(entry.queuedAt))) };
}

function elapsed(milliseconds: number): string {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export function DesignProgress({
  value,
  starting = false,
  selectionReady = false,
}: {
  value: ProgressValue;
  starting?: boolean;
  /** Both ZIP choices are already made — do not ask the operator to pick again. */
  selectionReady?: boolean;
}): ReactNode {
  // Keep older work visible when another refinement starts before it has finished.
  const work = ([{ detail: value.icon, icon: true }, { detail: value.screenshots, icon: false }]).flatMap(({ detail, icon }) =>
    detail.batches.map((batch) => batchWork(value, batch, icon))
      .filter((entry, index) => entry.total > 0 && (entry.active || index === detail.batches.length - 1)));
  const active = starting || work.some((entry) => entry.active);
  const interrupted = work.some((entry) => entry.interrupted);
  const completed = work.reduce((sum, entry) => sum + entry.completed, 0);
  const total = work.reduce((sum, entry) => sum + entry.total, 0);
  const since = Math.min(...work.filter((entry) => entry.active).map((entry) => entry.since));
  const panel = useRef<HTMLElement>(null);
  const previousActive = useRef(new Set<string>());
  const activeIds = work.filter((entry) => entry.active).map((entry) => entry.id).join(',');
  useEffect(() => {
    const current = new Set(activeIds.split(',').filter(Boolean));
    const added = [...current].some((id) => !previousActive.current.has(id));
    previousActive.current = current;
    // Reveal a new run, never move the operator's view for a completed frame or timer tick.
    if (starting || added) panel.current?.scrollIntoView?.({ block: 'nearest' });
  }, [starting, activeIds]);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  if (work.length === 0 && !starting) return null;
  return <section ref={panel} className={`design-progress${active ? ' is-active' : interrupted ? ' is-interrupted' : ' is-complete'}`} aria-label="Ход генерации">
    <div className="design-progress-head" role="status"><h2>{active ? <span className="design-progress-spinner" aria-hidden="true" /> : null}
      {starting ? 'Готовим запуск' : active ? 'Создаём изображения' : interrupted ? 'Создание остановлено' : 'Изображения готовы'}</h2>
      {total > 0 ? <strong>{`Готово изображений: ${completed} из ${total}`}</strong> : null}</div>
    {total > 0 ? <progress max={total} value={completed} aria-label="Сохранённые изображения" />
      : <progress aria-label="Подготовка генерации" />}
    <ol className="design-progress-stages" aria-label="Этапы создания изображений">{work.map((entry) => <li key={entry.id}>
      <div><strong>{entry.label}</strong><span>{entry.stage}</span></div><span>{`${entry.completed} / ${entry.total}`}</span>
    </li>)}</ol>
    {work.filter((entry) => entry.previews.length > 0).map((entry) => <div className="design-progress-previews" key={entry.id}>
      <p className="note">{`${entry.label}: готовые кадры. Нажмите на изображение, чтобы рассмотреть.`}</p>
      <ol className="design-ribbon design-ribbon-large" aria-label={`Готовые кадры: ${entry.label}`}>{entry.previews.map((image) =>
        <li key={`${image.slotId}:${image.position}`}><a href={`${ARTIFACTS_PATH}/${image.artifactId}`} target="_blank" rel="noreferrer">
          <img src={`${ARTIFACTS_PATH}/${image.artifactId}`} alt={`Готовый скриншот ${image.position + 1}`} /></a><span>{image.position + 1}</span></li>)}</ol>
    </div>)}
    {active ? <p className="note">{Number.isFinite(since) ? <span className="design-progress-time">{`Прошло ${elapsed(now - since)}. `}</span> : null}
      Одно изображение может создаваться несколько минут. Можно закрыть страницу — работа продолжится, а результат сохранится.</p>
      : <p className="note">{interrupted ? 'Готовые изображения сохранены. Причина остановки и действия для продолжения указаны ниже.'
        : selectionReady
          ? 'Иконка и набор скриншотов выбраны. Подготовьте ZIP ниже.'
          : 'Выберите иконку и набор скриншотов ниже, чтобы подготовить ZIP.'}</p>}
  </section>;
}
